import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { money, number, thaiDateTime } from '../lib/format'

/**
 * Payment gateway console (part 85).
 *
 * The page drives the authorisation conversation, not the invoice ledger: picking an invoice
 * only chooses the receivable an intent points at. Money is entered in baht and converted to
 * integer satang with a string parser, never with parseFloat; the fee preview uses the same
 * half-up integer rule as domain/gateway so the number on screen matches the captured row.
 *
 * The API mount (MountBillingGatewayRoutes) is intentionally not called from server.go in this
 * part and this page is not routed from App.tsx; both are one-line wiring changes, reviewed when
 * the first operator needs the console.
 */

type IntentStatus = 'requires_payment' | 'processing' | 'succeeded' | 'failed'
type ThreeDSStatus = 'not_required' | 'required' | 'passed'
type Provider = 'omise' | 'stripe' | 'mock'

interface GatewayIntent {
  id: string
  invoice_id: string
  provider: Provider
  provider_ref?: string
  amount_satang: number
  currency: string
  status: IntentStatus
  requires_three_ds: boolean
  three_ds_status: ThreeDSStatus
  three_ds_challenge?: string
  payment_method_id?: string
  captured_satang: number
  refunded_satang: number
  fee_satang: number
  fee_percent_bp: number
  fee_fixed_satang: number
  failure_reason?: string
  attempt_count: number
  next_retry_at?: string
  created_at: string
  updated_at: string
  captured_at?: string
  succeeded_at?: string
  refundable_satang: number
  net_satang: number
  authorized_remaining_satang: number
}

interface FeePolicy {
  percent_bp: number
  fixed_satang: number
}

interface GatewayList {
  intents: GatewayIntent[]
  count: number
  fee_policy: FeePolicy
  retry_delays_seconds: number[]
  note: string
  server_at: string
}

interface InvoiceRow {
  id: string
  number: string
  currency: string
  total_satang: number
  status: string
  issued_at: string
}

interface RefundResponse {
  refund: {
    id: string
    intent_id: string
    amount_satang: number
    status: string
    reason?: string
    created_at: string
  }
  intent: GatewayIntent
}

const INTENTS_KEY = ['billing-gateway-intents']
const INVOICES_KEY = ['billing-gateway-invoices']

const STATUS_LABEL: Record<IntentStatus, string> = {
  requires_payment: 'รอการชำระเงิน',
  processing: 'กำลังดำเนินการ',
  succeeded: 'จับเงินแล้ว',
  failed: 'ไม่สำเร็จ',
}

const PROVIDER_LABEL: Record<Provider, string> = {
  omise: 'Omise',
  stripe: 'Stripe',
  mock: 'Mock (ทดสอบ)',
}

const defaultPolicy: FeePolicy = { percent_bp: 290, fixed_satang: 1000 }

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : 'เกิดข้อผิดพลาด กรุณาลองอีกครั้ง'
}

/** Parses a baht string with at most two decimals into integer satang without floating point. */
function bahtToSatang(raw: string): number {
  const value = raw.trim().replace(/,/g, '')
  if (!/^\d+(\.\d{0,2})?$/.test(value)) return Number.NaN
  const [baht, fraction = ''] = value.split('.')
  return Number(baht) * 100 + Number((fraction + '00').slice(0, 2))
}

/** Renders satang as a baht input value with two decimals. */
function satangToBaht(satang: number): string {
  return (satang / 100).toFixed(2)
}

function amountLabel(satang: number, currency: string): string {
  if (currency === 'THB') return money(satang)
  return `${money(satang, { symbol: false })} ${currency}`
}

/** Mirrors gateway.FeePolicy.Fee: half-up basis points, then the fixed part, capped at amount. */
function feeFor(amount: number, policy: FeePolicy): number {
  if (amount <= 0) return 0
  const percent = Math.floor((amount * policy.percent_bp + 5000) / 10000)
  return Math.min(percent + policy.fixed_satang, amount)
}

function feeFormula(policy: FeePolicy): string {
  const percent = (policy.percent_bp / 100).toFixed(2)
  return `${percent}% + ${money(policy.fixed_satang)} ต่อรายการ`
}

function delayLabel(seconds: number): string {
  if (seconds >= 3600 && seconds % 3600 === 0) return `${seconds / 3600} ชั่วโมง`
  if (seconds >= 60 && seconds % 60 === 0) return `${seconds / 60} นาที`
  return `${seconds} วินาที`
}

export function PaymentGateway() {
  const { me } = useAuth()
  const qc = useQueryClient()
  const canManage = me?.role === 'owner' || me?.role === 'admin'

  const intentsQ = useQuery({
    queryKey: INTENTS_KEY,
    queryFn: () => api<GatewayList>('/v1/billing/gateway/intents?limit=200'),
    staleTime: 10_000,
  })
  const invoicesQ = useQuery({
    queryKey: INVOICES_KEY,
    queryFn: () => api<InvoiceRow[]>('/v1/invoices?limit=100'),
    staleTime: 60_000,
  })

  const intents = intentsQ.data?.intents ?? []
  const invoices = invoicesQ.data ?? []
  const policy = intentsQ.data?.fee_policy ?? defaultPolicy
  const retryDelays = intentsQ.data?.retry_delays_seconds ?? []

  const [invoiceId, setInvoiceId] = useState('')
  const [amountBaht, setAmountBaht] = useState('')
  const [provider, setProvider] = useState<Provider>('mock')
  const [requiresThreeDS, setRequiresThreeDS] = useState(false)

  const [selectedId, setSelectedId] = useState('')
  const [threeDSToken, setThreeDSToken] = useState('')
  const [captureBaht, setCaptureBaht] = useState('')
  const [refundBaht, setRefundBaht] = useState('')
  const [refundReason, setRefundReason] = useState('')
  const [notice, setNotice] = useState('')

  const selectedInvoice = invoices.find((inv) => inv.id === invoiceId) ?? null
  const selected = useMemo(
    () => intents.find((row) => row.id === selectedId) ?? null,
    [intents, selectedId],
  )

  // Default the create form to the newest invoice, without overwriting a choice the operator
  // already made.
  useEffect(() => {
    if (invoiceId || invoices.length === 0) return
    setInvoiceId(invoices[0].id)
    setAmountBaht(satangToBaht(invoices[0].total_satang))
  }, [invoices, invoiceId])

  // When a different intent is selected, seed the capture and refund inputs with the full
  // remaining amounts. The operator can lower them for a partial capture or refund.
  useEffect(() => {
    if (!selected) return
    setCaptureBaht(satangToBaht(selected.authorized_remaining_satang))
    setRefundBaht(satangToBaht(selected.refundable_satang))
    setThreeDSToken('')
    setRefundReason('')
    setNotice('')
  }, [selected?.id, selected?.status])

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: INTENTS_KEY })
  }

  const createIntent = useMutation({
    mutationFn: () => {
      if (!selectedInvoice) throw new Error('เลือกใบแจ้งหนี้ก่อนสร้างรายการ')
      const amount = bahtToSatang(amountBaht)
      if (!Number.isFinite(amount) || amount <= 0) throw new Error('จำนวนเงินไม่ถูกต้อง')
      return apiWrite<GatewayIntent>('/v1/billing/gateway/intents', 'POST', {
        invoice_id: selectedInvoice.id,
        amount_satang: amount,
        currency: selectedInvoice.currency,
        provider,
        requires_three_ds: requiresThreeDS,
      })
    },
    onSuccess: (intent) => {
      setSelectedId(intent.id)
      invalidate()
    },
  })

  const confirmIntent = useMutation({
    mutationFn: (outcome: 'approve' | 'decline') => {
      if (!selected) throw new Error('ยังไม่ได้เลือกรายการ')
      if (selected.requires_three_ds && selected.three_ds_status !== 'passed' && !threeDSToken.trim()) {
        throw new Error('รายการนี้ต้องยืนยัน 3-D Secure ก่อน')
      }
      return apiWrite<GatewayIntent>(`/v1/billing/gateway/intents/${selected.id}/confirm`, 'POST', {
        three_ds_token: threeDSToken.trim(),
        outcome,
      })
    },
    onSuccess: () => {
      setThreeDSToken('')
      invalidate()
    },
  })

  const captureIntent = useMutation({
    mutationFn: () => {
      if (!selected) throw new Error('ยังไม่ได้เลือกรายการ')
      const amount = bahtToSatang(captureBaht)
      if (!Number.isFinite(amount) || amount < 0) throw new Error('จำนวนเงินไม่ถูกต้อง')
      return apiWrite<GatewayIntent>(`/v1/billing/gateway/intents/${selected.id}/capture`, 'POST', {
        amount_satang: amount,
      })
    },
    onSuccess: (updated) => {
      // After a capture the refund panel becomes available; seed it with the full captured
      // amount so the common case is one click, not a typed figure.
      setRefundBaht(satangToBaht(updated.refundable_satang))
      setCaptureBaht(satangToBaht(updated.authorized_remaining_satang))
      invalidate()
    },
  })

  const refundIntent = useMutation({
    mutationFn: () => {
      if (!selected) throw new Error('ยังไม่ได้เลือกรายการ')
      const amount = bahtToSatang(refundBaht)
      if (!Number.isFinite(amount) || amount <= 0) throw new Error('จำนวนเงินไม่ถูกต้อง')
      if (amount > selected.refundable_satang) throw new Error('จำนวนเงินเกินยอดที่คืนได้')
      return apiWrite<RefundResponse>(`/v1/billing/gateway/intents/${selected.id}/refund`, 'POST', {
        amount_satang: amount,
        reason: refundReason.trim(),
      })
    },
    onSuccess: (result) => {
      setNotice(`คืนเงิน ${amountLabel(result.refund.amount_satang, selected?.currency ?? 'THB')} สำเร็จ`)
      setRefundBaht('')
      setRefundReason('')
      invalidate()
    },
  })

  const totals = useMemo(() => {
    let pending = 0
    let pendingCount = 0
    let captured = 0
    let refunded = 0
    let fees = 0
    for (const row of intents) {
      if (row.status === 'requires_payment' || row.status === 'processing') {
        pending += row.amount_satang
        pendingCount += 1
      }
      captured += row.captured_satang
      refunded += row.refunded_satang
      fees += row.fee_satang
    }
    return { pending, pendingCount, captured, refunded, fees }
  }, [intents])

  const createAmount = bahtToSatang(amountBaht)
  const previewFee = Number.isFinite(createAmount) ? feeFor(createAmount, policy) : 0
  const canCreate = canManage && selectedInvoice !== null && Number.isFinite(createAmount) && createAmount > 0

  if (intentsQ.isLoading) {
    return (
      <>
        <PageHead title="ประตูชำระเงิน" sub="สร้างรายการอนุมัติ จับเงิน และคืนเงิน" />
        <div className="stack"><Skel h={120} /><Skel h={280} /></div>
      </>
    )
  }

  if (intentsQ.isError || !intentsQ.data) {
    return (
      <>
        <PageHead title="ประตูชำระเงิน" sub="สร้างรายการอนุมัติ จับเงิน และคืนเงิน" />
        <Empty title="โหลดรายการชำระเงินไม่ได้" body="ลองรีเฟรชหน้าอีกครั้ง หรือตรวจสอบการเชื่อมต่อกับเซิร์ฟเวอร์" />
      </>
    )
  }

  return (
    <>
      <PageHead
        title="ประตูชำระเงิน"
        sub="สร้างรายการอนุมัติ จับเงิน และคืนเงิน โดยคิดค่าธรรมเนียมจากยอดที่จับได้จริง"
        actions={
          <button className="btn" type="button" onClick={() => invalidate()} disabled={intentsQ.isFetching}>
            {intentsQ.isFetching ? 'กำลังโหลด…' : 'รีเฟรช'}
          </button>
        }
      />

      <div className="grid-4">
        <Stat
          label="รอชำระเงิน"
          value={<span className="tnum">{totals.pendingCount}</span>}
          unit="รายการ"
          foot={`ยอดอนุมัติรวม ${money(totals.pending)}`}
        />
        <Stat label="จับเงินแล้ว" value={<span className="tnum">{money(totals.captured, { compact: true })}</span>} foot="ยอดที่เรียกเก็บได้จริง" />
        <Stat label="คืนเงินแล้ว" value={<span className="tnum">{money(totals.refunded, { compact: true })}</span>} foot="ไม่เกินยอดที่จับเงินได้" />
        <Stat label="ค่าธรรมเนียม" value={<span className="tnum">{money(totals.fees, { compact: true })}</span>} foot={feeFormula(policy)} />
      </div>

      <div className="grid-main" style={{ marginTop: 16 }}>
        <div className="card card-pad">
          <div className="card-label">สร้างรายการอนุมัติใหม่</div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.8 }}>
            เลือกใบแจ้งหนี้ที่ต้องการเรียกเก็บ ระบบจะผูกเงินเข้ากับใบแจ้งหนี้โดยไม่แก้ไขใบแจ้งหนี้เดิม
            จำนวนเงินเป็นบาทและถูกแปลงเป็นสตางค์แบบจำนวนเต็ม
          </div>

          <form
            style={{ marginTop: 8 }}
            onSubmit={(e) => {
              e.preventDefault()
              if (canCreate) createIntent.mutate()
            }}
          >
            <div className="field">
              <label htmlFor="gw-invoice">ใบแจ้งหนี้</label>
              {invoicesQ.isLoading ? (
                <Skel h={42} />
              ) : invoices.length === 0 ? (
                <div className="dim" style={{ fontSize: 12 }}>ยังไม่มีใบแจ้งหนี้ในระบบ</div>
              ) : (
                <select
                  id="gw-invoice"
                  className="input"
                  value={invoiceId}
                  disabled={!canManage}
                  onChange={(e) => {
                    const next = invoices.find((inv) => inv.id === e.target.value) ?? null
                    setInvoiceId(e.target.value)
                    if (next) setAmountBaht(satangToBaht(next.total_satang))
                  }}
                >
                  {invoices.map((inv) => (
                    <option key={inv.id} value={inv.id}>
                      {inv.number} · {amountLabel(inv.total_satang, inv.currency)}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="gw-amount">จำนวนเงิน (บาท)</label>
                <input
                  id="gw-amount"
                  className="input mono"
                  inputMode="decimal"
                  placeholder="1,000.00"
                  value={amountBaht}
                  disabled={!canManage}
                  onChange={(e) => setAmountBaht(e.target.value)}
                />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="gw-provider">ผู้ให้บริการ</label>
                <select
                  id="gw-provider"
                  className="input"
                  value={provider}
                  disabled={!canManage}
                  onChange={(e) => setProvider(e.target.value as Provider)}
                >
                  {(['mock', 'omise', 'stripe'] as Provider[]).map((p) => (
                    <option key={p} value={p}>{PROVIDER_LABEL[p]}</option>
                  ))}
                </select>
              </div>
            </div>

            <label className="row" style={{ gap: 8, marginTop: 14, fontSize: 12, color: 'var(--color-ivory-muted)' }}>
              <input
                type="checkbox"
                checked={requiresThreeDS}
                disabled={!canManage}
                onChange={(e) => setRequiresThreeDS(e.target.checked)}
              />
              ต้องยืนยัน 3-D Secure ก่อนจับเงิน
            </label>

            <div className="row" style={{ marginTop: 16, justifyContent: 'space-between', fontSize: 12 }}>
              <span className="dim">ค่าธรรมเนียมโดยประมาณ</span>
              <span className="tnum">{Number.isFinite(createAmount) && createAmount > 0 ? money(previewFee) : '—'}</span>
            </div>
            <div className="row" style={{ marginTop: 6, justifyContent: 'space-between', fontSize: 12 }}>
              <span className="dim">ยอดสุทธิที่จะได้รับ</span>
              <span className="tnum">
                {Number.isFinite(createAmount) && createAmount > 0 ? money(createAmount - previewFee) : '—'}
              </span>
            </div>

            <button
              className="btn btn-primary"
              type="submit"
              style={{ width: '100%', marginTop: 16 }}
              disabled={!canCreate || createIntent.isPending}
            >
              {createIntent.isPending ? 'กำลังสร้าง…' : 'สร้างรายการอนุมัติ'}
            </button>
            {!canManage ? (
              <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
                เฉพาะเจ้าของและผู้ดูแลระบบเท่านั้นที่สร้างรายการและเคลื่อนเงินได้
              </div>
            ) : null}
            {createIntent.isError ? <div className="form-error">{errorText(createIntent.error)}</div> : null}
          </form>
        </div>

        <div className="card card-pad">
          <div className="card-label">จัดการรายการที่เลือก</div>
          {!selected ? (
            <div style={{ marginTop: 14 }}>
              <Empty title="ยังไม่ได้เลือกรายการ" body="เลือกแถวในตารางเพื่อยืนยัน จับเงิน หรือคืนเงิน" />
            </div>
          ) : (
            <>
              <div className="row" style={{ marginTop: 12, justifyContent: 'space-between' }}>
                <span className="mono">{selected.id.slice(0, 12)}…</span>
                <span className={`chip ${selected.status === 'succeeded' ? 'gold' : ''}`}>
                  {STATUS_LABEL[selected.status]}
                </span>
              </div>
              <div className="stack" style={{ gap: 8, marginTop: 14, fontSize: 12 }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="dim">ยอดอนุมัติ</span>
                  <span className="tnum">{amountLabel(selected.amount_satang, selected.currency)}</span>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="dim">จับเงินได้</span>
                  <span className="tnum">{amountLabel(selected.captured_satang, selected.currency)}</span>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="dim">คืนเงินแล้ว</span>
                  <span className="tnum">{amountLabel(selected.refunded_satang, selected.currency)}</span>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="dim">ค่าธรรมเนียม</span>
                  <span className="tnum">{amountLabel(selected.fee_satang, selected.currency)}</span>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="dim">ยอดสุทธิ</span>
                  <span className="tnum">{amountLabel(selected.net_satang, selected.currency)}</span>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="dim">3-D Secure</span>
                  <span>{selected.requires_three_ds ? (selected.three_ds_status === 'passed' ? 'ผ่านแล้ว' : 'รอยืนยัน') : 'ไม่ต้องใช้'}</span>
                </div>
              </div>

              {selected.status === 'requires_payment' ? (
                <form
                  style={{ marginTop: 16 }}
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (canManage) confirmIntent.mutate('approve')
                  }}
                >
                  {selected.requires_three_ds ? (
                    <div className="field" style={{ marginTop: 0 }}>
                      <label htmlFor="gw-3ds">Token จากหน้าต่าง 3-D Secure</label>
                      <input
                        id="gw-3ds"
                        className="input mono"
                        placeholder="tok_..."
                        value={threeDSToken}
                        disabled={!canManage}
                        onChange={(e) => setThreeDSToken(e.target.value)}
                      />
                      <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
                        challenge {selected.three_ds_challenge ?? '—'}
                      </div>
                    </div>
                  ) : null}
                  <div className="row" style={{ gap: 8, marginTop: 14 }}>
                    <button className="btn btn-primary" type="submit" disabled={!canManage || confirmIntent.isPending} style={{ flex: 1 }}>
                      {confirmIntent.isPending ? 'กำลังยืนยัน…' : 'อนุมัติ'}
                    </button>
                    <button
                      className="btn"
                      type="button"
                      disabled={!canManage || confirmIntent.isPending}
                      onClick={() => confirmIntent.mutate('decline')}
                    >
                      ปฏิเสธ
                    </button>
                  </div>
                  {confirmIntent.isError ? <div className="form-error">{errorText(confirmIntent.error)}</div> : null}
                </form>
              ) : null}

              {selected.status === 'processing' ? (
                <form
                  style={{ marginTop: 16 }}
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (canManage) captureIntent.mutate()
                  }}
                >
                  <div className="field" style={{ marginTop: 0 }}>
                    <label htmlFor="gw-capture">ยอดที่จับเงิน (บาท)</label>
                    <input
                      id="gw-capture"
                      className="input mono"
                      inputMode="decimal"
                      value={captureBaht}
                      disabled={!canManage}
                      onChange={(e) => setCaptureBaht(e.target.value)}
                    />
                    <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
                      จับได้ไม่เกิน {amountLabel(selected.authorized_remaining_satang, selected.currency)} ·
                      จับบางส่วนแล้วส่วนที่เหลือจะถูกปล่อย
                    </div>
                  </div>
                  <button className="btn btn-primary" type="submit" disabled={!canManage || captureIntent.isPending} style={{ width: '100%', marginTop: 14 }}>
                    {captureIntent.isPending ? 'กำลังจับเงิน…' : 'จับเงิน'}
                  </button>
                  {captureIntent.isError ? <div className="form-error">{errorText(captureIntent.error)}</div> : null}
                </form>
              ) : null}

              {selected.status === 'succeeded' && selected.refundable_satang > 0 ? (
                <form
                  style={{ marginTop: 16 }}
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (canManage) refundIntent.mutate()
                  }}
                >
                  <div className="field" style={{ marginTop: 0 }}>
                    <label htmlFor="gw-refund">ยอดคืนเงิน (บาท)</label>
                    <input
                      id="gw-refund"
                      className="input mono"
                      inputMode="decimal"
                      value={refundBaht}
                      disabled={!canManage}
                      onChange={(e) => setRefundBaht(e.target.value)}
                    />
                    <div className="dim" style={{ fontSize: 11, marginTop: 6 }}>
                      คืนได้อีก {amountLabel(selected.refundable_satang, selected.currency)} จากยอดที่จับเงินได้
                    </div>
                  </div>
                  <div className="field">
                    <label htmlFor="gw-refund-reason">เหตุผล (ไม่บังคับ)</label>
                    <input
                      id="gw-refund-reason"
                      className="input"
                      placeholder="เช่น ลูกค้ายกเลิกบริการ"
                      value={refundReason}
                      disabled={!canManage}
                      onChange={(e) => setRefundReason(e.target.value)}
                    />
                  </div>
                  <button className="btn btn-primary" type="submit" disabled={!canManage || refundIntent.isPending} style={{ width: '100%' }}>
                    {refundIntent.isPending ? 'กำลังคืนเงิน…' : 'คืนเงิน'}
                  </button>
                  {refundIntent.isError ? <div className="form-error">{errorText(refundIntent.error)}</div> : null}
                  {notice ? <div className="chip gold" style={{ marginTop: 12 }}>{notice}</div> : null}
                </form>
              ) : null}

              {selected.status === 'succeeded' && selected.refundable_satang === 0 ? (
                <div className="dim" style={{ fontSize: 11.5, marginTop: 14 }}>
                  รายการนี้คืนเงินครบทั้งยอดที่จับได้แล้ว ไม่มียอดคงเหลือให้คืน
                </div>
              ) : null}

              {selected.status === 'failed' ? (
                <div style={{ marginTop: 14 }}>
                  <div className="dim" style={{ fontSize: 12, lineHeight: 1.8 }}>
                    ปฏิเสธ: {selected.failure_reason || 'ไม่ทราบสาเหตุ'} · พยายามแล้ว {selected.attempt_count} ครั้ง
                  </div>
                  <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>
                    {selected.next_retry_at
                      ? `รอบถัดไป ${thaiDateTime(selected.next_retry_at)}`
                      : 'ครบจำนวนครั้งที่ลองแล้ว ต้องสร้างรายการใหม่'}
                  </div>
                  {selected.next_retry_at ? (
                    <button
                      className="btn"
                      type="button"
                      style={{ width: '100%', marginTop: 12 }}
                      disabled={!canManage || confirmIntent.isPending}
                      onClick={() => confirmIntent.mutate('approve')}
                    >
                      ลองใหม่ตอนนี้
                    </button>
                  ) : null}
                  {confirmIntent.isError ? <div className="form-error">{errorText(confirmIntent.error)}</div> : null}
                </div>
              ) : null}
            </>
          )}

          {retryDelays.length > 0 ? (
            <div className="dim" style={{ fontSize: 10.5, marginTop: 18, lineHeight: 1.7 }}>
              ตารางลองใหม่เมื่อถูกปฏิเสธ: {retryDelays.map(delayLabel).join(' · ')}
            </div>
          ) : null}
        </div>
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <div className="card-label">รายการล่าสุด</div>
          <span className="dim" style={{ fontSize: 11.5 }}>{number(intents.length)} รายการ</span>
        </div>
        {intents.length === 0 ? (
          <div style={{ marginTop: 14 }}>
            <Empty title="ยังไม่มีรายการชำระเงิน" body="สร้างรายการอนุมัติจากใบแจ้งหนี้เพื่อเริ่มต้น" />
          </div>
        ) : (
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="data">
              <thead>
                <tr>
                  <th>สร้างเมื่อ</th>
                  <th>ผู้ให้บริการ</th>
                  <th className="num">ยอดอนุมัติ</th>
                  <th>สถานะ</th>
                  <th className="num">จับเงินได้</th>
                  <th className="num">คืนแล้ว</th>
                  <th className="num">ค่าธรรมเนียม</th>
                  <th className="num">สุทธิ</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {intents.map((row) => (
                  <tr key={row.id} onClick={() => setSelectedId(row.id)}>
                    <td className="dim" style={{ whiteSpace: 'nowrap' }}>{thaiDateTime(row.created_at)}</td>
                    <td>
                      <div>{PROVIDER_LABEL[row.provider] ?? row.provider}</div>
                      <div className="dim" style={{ fontSize: 10.5 }}>
                        {row.requires_three_ds ? (row.three_ds_status === 'passed' ? '3DS ผ่าน' : 'รอ 3DS') : 'ไม่ใช้ 3DS'}
                      </div>
                    </td>
                    <td className="num tnum" style={{ whiteSpace: 'nowrap' }}>{amountLabel(row.amount_satang, row.currency)}</td>
                    <td>
                      <span className={`chip ${row.status === 'succeeded' ? 'gold' : ''}`}>
                        {STATUS_LABEL[row.status]}
                      </span>
                    </td>
                    <td className="num tnum">{row.captured_satang > 0 ? amountLabel(row.captured_satang, row.currency) : '—'}</td>
                    <td className="num tnum">{row.refunded_satang > 0 ? amountLabel(row.refunded_satang, row.currency) : '—'}</td>
                    <td className="num tnum">{row.fee_satang > 0 ? amountLabel(row.fee_satang, row.currency) : '—'}</td>
                    <td className="num tnum">{row.captured_satang > 0 ? amountLabel(row.net_satang, row.currency) : '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="btn btn-sm"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedId(row.id)
                        }}
                      >
                        จัดการ
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="dim" style={{ fontSize: 10.5, marginTop: 14, lineHeight: 1.7 }}>
          {intentsQ.data.note}
        </div>
      </div>
    </>
  )
}
